import React from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";

import AccessibilityIcon from "../../assets/ui/accessibility-logo.svg";
import { Menu } from "../general-components/navigation/menu";
import { useDispatch, useSelector } from "react-redux";
import { accessibilityMenuOpenAction } from "../app-actions";
import useAccessibilityContext from "../accessibility/store/use-accessibility-context";
import FontSizeSettings from "@default/js/accessibility/font-size-settings";
import ThemeSwitch from "@default/js/accessibility/theme-switch";
import { FocusTrap } from "focus-trap-react";
import useTranslate from "@default/hooks/use-translate";
import { motion } from "framer-motion";
import DashedBorder from "@default/general-components/dashed-border";

/**
 * Close on menu link click.
 */
const AccessibilityMenu = () => {
  const dispatch = useDispatch();
  const { simpleDesign, toggleSimpleDesign } = useAccessibilityContext();
  const _t = useTranslate();
  const microSite = useSelector((reduxStore) => reduxStore.appStore.microSite);
  const currentLanguage = useSelector(
    (reduxStore) => reduxStore.i18n.currentLanguage
  );

  const accessibilityMenuOpen = useSelector(
    (reduxStore) => reduxStore.appStore.accessibilityMenuOpen
  );

  /**
   * Toggle the accessibility menu.
   *
   * @param {boolean|undefined} openItArg - If true, the menu will be opened; if false, it will be closed;
   * if undefined, it will toggle the current state.
   */
  const toggleAccessibilityMenu = (openItArg) => {
    // If the main menu is open, close
    let openIt = null;
    switch (typeof openItArg) {
      case "boolean":
        // If a boolean is passed, use it to set the state.
        openIt = openItArg;
        break;
      case "undefined":
        // If no argument is passed, toggle the current state.
        openIt = !accessibilityMenuOpen;
        break;
      default:
        console.error(
          'toggleAccessibilityMenu called with unexpected type: "%s": %o',
          typeof openItArg,
          openItArg
        );
        return;
    }
    dispatch(accessibilityMenuOpenAction(openIt));
  };

  return (
    <>
      <button
        id="accessibility-menu-toggle"
        className={"accessibility-button"}
        onClick={() => toggleAccessibilityMenu()}
        aria-label={_t("navi.open-accessibility-menu")}
      >
        <AccessibilityIcon alt={_t("navi.button-accessibility-settings")} />
      </button>

      {typeof window !== "undefined" &&
        createPortal(
          <FocusTrap
            active={accessibilityMenuOpen}
            focusTrapOptions={{
              onDeactivate: () => {
                toggleAccessibilityMenu(false);
              },
              clickOutsideDeactivates: (e) =>
                !e.target.closest("#accessibility-menu-toggle"),
              allowOutsideClick: (e) =>
                !!e.target.closest("#accessibility-menu-toggle"),
              escapeDeactivates: true,
            }}
          >
            <div
              id="accessibility-menu"
              className={accessibilityMenuOpen ? "active" : undefined}
              aria-hidden={!accessibilityMenuOpen}
              tabIndex={accessibilityMenuOpen ? undefined : -1}
              inert={!accessibilityMenuOpen ? "" : undefined}
            >
              <DashedBorder blurredBackdrop pill={false} />
              <motion.div
                className="header-element accessibility"
                initial={false}
                animate={
                  accessibilityMenuOpen ? { opacity: 1 } : { opacity: 0 }
                }
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="wrapper">
                  <nav className={"accessibility-settings"}>
                    <ul className="menu-list">
                      {!microSite && (
                        <li className="simple-design-toggle">
                          <div className="col">
                            <button
                              className={"toggle simple-design"}
                              data-active={simpleDesign}
                              id="simple-design-toggle"
                              onClick={() => toggleSimpleDesign()}
                              aria-label={`${_t(
                                "accessibility.simple_design"
                              )} ${
                                simpleDesign ? _t("disable") : _t("enable")
                              }`}
                            >
                              <span className="sr-only">
                                {`${_t("accessibility.simple_design")} ${
                                  simpleDesign ? _t("disable") : _t("enable")
                                }`}
                              </span>
                            </button>
                          </div>

                          <div className="col">
                            <span>{_t("accessibility.simple_design")}</span>
                          </div>
                        </li>
                      )}
                      <ThemeSwitch />
                      <FontSizeSettings min={1} max={2} step={0.1} />
                    </ul>
                  </nav>
                  {currentLanguage !== "en" && (
                    <Menu
                      navClassName="main-menu accessibility-menu"
                      menuName={
                        microSite === "rbx-company"
                          ? "ACCESSIBILITY_MENU_RBX"
                          : "ACCESSIBILITY_MENU"
                      }
                      ariaLabel={_t("navi.accessibility-menu")}
                    />
                  )}
                </div>
              </motion.div>
            </div>
          </FocusTrap>,
          document.body
        )}
    </>
  );
};

AccessibilityMenu.propTypes = {
  accessibilityMenuOpen: PropTypes.bool,
  accessibilityMenu: PropTypes.object,
};

export default AccessibilityMenu;
